import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBranding } from '../../hooks/useBranding';
import {
  Shield,
  Users,
  ChevronRight,
  Check,
  X,
  Save,
  AlertCircle,
  Info,
  Lock,
  Unlock,
  Plus,
  Trash2,
  Loader2
} from 'lucide-react';
import api from '../../services/api';
import { adminRolesAPI } from '../../services/api';

// System roles that cannot be deleted
const SYSTEM_ROLES = ['end_user', 'team_member', 'billing', 'client_technician', 'company_admin', 'finance', 'technician', 'admin'];

/**
 * Human-readable labels for permission resources
 */
const RESOURCE_LABELS = {
  tickets: 'Tickets',
  invoices: 'Invoices',
  payments: 'Payments',
  quotes: 'Quotes',
  agreements: 'Agreements',
  assets: 'Assets',
  projects: 'Projects',
  admin: 'Admin Panel',
  users: 'User Management',
  company_users: 'Company Users',
};

/**
 * Human-readable labels for permission actions
 */
const ACTION_LABELS = {
  read: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
  all_companies: 'All Companies',
  refund: 'Refund',
  manage: 'Manage',
  accept: 'Accept',
  dashboard: 'Dashboard',
  companies: 'Companies',
  users: 'Users',
  tickets: 'Tickets',
  invoices: 'Invoices',
  payments: 'Payments',
  audit: 'Audit Log',
  settings: 'Settings',
  roles: 'Roles',
  manage_roles: 'Manage Roles',
  quotes: 'Quotes',
  agreements: 'Agreements',
  sessions: 'Sessions',
  integrations: 'Integrations',
  accounts_receivable: 'Accounts Receivable',
  email_templates: 'Email Templates',
  api_keys: 'API Keys',
  feature_flags: 'Feature Flags',
  asset_types: 'Asset Types',
  time_tracking: 'Time Tracking',
};

/**
 * Role badge colors
 */
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  technician: 'bg-purple-100 text-purple-700 border-purple-200',
  finance: 'bg-green-100 text-green-700 border-green-200',
  company_admin: 'bg-blue-100 text-blue-700 border-blue-200',
  user: 'bg-neutral-100 text-neutral-700 border-neutral-200',
};

/**
 * Permission Editor Component
 */
/**
 * What a customer role can be given, and what each choice means to the MSP.
 * The server enforces exactly these (backend services/customerAccess.js);
 * staff-only actions (delete, refund, all companies) mean nothing here.
 */
const CUSTOMER_SCHEMA = {
  // work / escalate / log_time: a co-managed client's IT technician.
  tickets: ['read', 'read_own', 'create', 'update', 'work', 'escalate', 'log_time'],
  projects: ['read', 'read_own'],
  invoices: ['read'],
  payments: ['read', 'create', 'manage'],
  quotes: ['read', 'accept'],
  agreements: ['read'],
  assets: ['read'],
  documentation: ['read'],
  vault: ['read'],
  contracts: ['read'],
  company_users: ['manage'],
};
const CUSTOMER_ACTION_LABELS = {
  'tickets:read': 'Whole company', 'tickets:read_own': 'Own only', 'tickets:create': 'Open new', 'tickets:update': 'Reply & close',
  'tickets:work': 'Work the queue (assign, status, team notes)', 'tickets:escalate': 'Escalate to you', 'tickets:log_time': 'Log time',
  'projects:read': 'Whole company', 'projects:read_own': 'Ones they are on',
  'payments:read': 'View history', 'payments:create': 'Pay', 'payments:manage': 'Saved methods & AutoPay',
  'quotes:accept': 'Approve & sign',
  'company_users:manage': 'Manage team',
};
/** Ticket and project scope is one choice, not two switches. */
const SCOPE_PAIR = { read: 'read_own', read_own: 'read' };

function PermissionEditor({ permissions, permissionSchema, onChange, disabled, customer = false }) {
  const schema = customer ? CUSTOMER_SCHEMA : permissionSchema;
  const label = (resource, action) =>
    (customer && CUSTOMER_ACTION_LABELS[`${resource}:${action}`]) || ACTION_LABELS[action] || action;
  const handleToggle = (resource, action) => {
    const current = permissions[resource] || [];
    let newActions = current.includes(action)
      ? current.filter(a => a !== action)
      : [...current, action];
    if (customer && SCOPE_PAIR[action] && (resource === 'tickets' || resource === 'projects') && newActions.includes(action)) {
      newActions = newActions.filter(a => a !== SCOPE_PAIR[action]);
    }

    onChange({
      ...permissions,
      [resource]: newActions,
    });
  };

  return (
    <div className="space-y-4">
      {Object.entries(schema).map(([resource, actions]) => (
        <div key={resource} className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
          <div className="bg-neutral-50 dark:bg-neutral-800 px-4 py-2 border-b border-neutral-200 dark:border-neutral-700">
            <h4 className="font-medium text-neutral-900 dark:text-white">
              {RESOURCE_LABELS[resource] || resource}
            </h4>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {actions.map(action => {
                const isEnabled = (permissions[resource] || []).includes(action);
                return (
                  <button
                    key={action}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleToggle(resource, action)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                      transition-colors border
                      ${isEnabled
                        ? 'bg-primary-100 text-primary-700 border-primary-300 dark:bg-primary-900/30 dark:text-primary-400 dark:border-primary-700'
                        : 'bg-neutral-100 text-neutral-500 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700'
                      }
                      ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'}
                    `}
                  >
                    {isEnabled ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                    {label(resource, action)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Role Card Component
 */
function RoleCard({ role, onClick }) {
  const permissionCount = Object.values(role.permissions || {}).flat().length;

  return (
    <div
      onClick={onClick}
      className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 cursor-pointer hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${role.isInternal ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
            {role.isInternal ? (
              <Lock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            ) : (
              <Unlock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-neutral-900 dark:text-white">
              {role.displayName}
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {role.description}
            </p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-neutral-400" />
      </div>

      <div className="mt-4 flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5 text-neutral-600 dark:text-neutral-300">
          <Users className="w-4 h-4" />
          <span>{role.userCount} user{role.userCount !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-1.5 text-neutral-600 dark:text-neutral-300">
          <Shield className="w-4 h-4" />
          <span>{permissionCount} permission{permissionCount !== 1 ? 's' : ''}</span>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${ROLE_COLORS[role.name] || ROLE_COLORS.user}`}>
          Level {role.hierarchyLevel}
        </span>
      </div>
    </div>
  );
}

/**
 * Role Create Modal
 */
function RoleCreateModal({ onClose, onCreated }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [hierarchyLevel, setHierarchyLevel] = useState(0);
  const [nameError, setNameError] = useState('');

  const createMutation = useMutation({
    mutationFn: (data) => adminRolesAPI.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      onCreated(response.data.role);
    }
  });

  const validateName = (value) => {
    if (!value) {
      setNameError('Name is required');
      return false;
    }
    if (!/^[a-z][a-z0-9_]*$/.test(value)) {
      setNameError('Name must start with lowercase letter and contain only lowercase letters, numbers, and underscores');
      return false;
    }
    if (SYSTEM_ROLES.includes(value)) {
      setNameError('This name is reserved for system roles');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleNameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
    setName(value);
    validateName(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateName(name)) return;
    if (!displayName) return;

    createMutation.mutate({
      name,
      displayName,
      description,
      isInternal,
      hierarchyLevel
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary-500" />
            Create New Role
          </h2>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* System Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              System Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="e.g. sales_rep"
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white ${
                nameError ? 'border-red-500' : 'border-neutral-300 dark:border-neutral-600'
              }`}
            />
            {nameError && (
              <p className="mt-1 text-sm text-red-500">{nameError}</p>
            )}
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Unique identifier used internally. Cannot be changed later.
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Display Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Sales Representative"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this role's purpose"
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            />
          </div>

          {/* Role Type */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Role Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!isInternal}
                  onChange={() => setIsInternal(false)}
                  className="w-4 h-4 text-primary-600"
                />
                <span className="flex items-center gap-1 text-sm text-neutral-700 dark:text-neutral-300">
                  <Unlock className="w-4 h-4 text-blue-500" />
                  Customer Role
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={isInternal}
                  onChange={() => setIsInternal(true)}
                  className="w-4 h-4 text-primary-600"
                />
                <span className="flex items-center gap-1 text-sm text-neutral-700 dark:text-neutral-300">
                  <Lock className="w-4 h-4 text-purple-500" />
                  Internal Staff Role
                </span>
              </label>
            </div>
          </div>

          {/* Hierarchy Level */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Hierarchy Level
            </label>
            <input
              type="number"
              min={0}
              max={10}
              value={hierarchyLevel}
              onChange={(e) => setHierarchyLevel(parseInt(e.target.value) || 0)}
              className="w-24 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            />
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Higher levels can access features of lower levels (0 = lowest, 10 = highest)
            </p>
          </div>

          {/* Error message */}
          {createMutation.isError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-sm text-red-700 dark:text-red-300">
                {createMutation.error?.response?.data?.error || 'Failed to create role'}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || !name || !displayName || !!nameError}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Role
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Role Edit Modal
 */
function RoleEditModal({ roleId, onClose }) {
  const queryClient = useQueryClient();
  const [editedPermissions, setEditedPermissions] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedDisplayName, setEditedDisplayName] = useState('');
  const [editedHierarchyLevel, setEditedHierarchyLevel] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-role', roleId],
    queryFn: async () => {
      const response = await api.get(`/admin/roles/${roleId}`);
      return response.data;
    },
  });

  // Initialize form state when data loads (React Query v5 removed onSuccess)
  useEffect(() => {
    if (data?.role) {
      setEditedPermissions(data.role.permissions);
      setEditedDescription(data.role.description || '');
      setEditedDisplayName(data.role.displayName);
      setEditedHierarchyLevel(data.role.hierarchyLevel);
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: async (updates) => {
      const response = await api.put(`/admin/roles/${roleId}`, updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      queryClient.invalidateQueries({ queryKey: ['admin-role', roleId] });
      setHasChanges(false);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => adminRolesAPI.delete(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      onClose();
    }
  });

  const isSystemRole = data?.role?.name && SYSTEM_ROLES.includes(data.role.name);
  const hasUsers = data?.users?.length > 0;

  const handlePermissionsChange = (newPermissions) => {
    setEditedPermissions(newPermissions);
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      displayName: editedDisplayName,
      description: editedDescription,
      permissions: editedPermissions,
      hierarchyLevel: editedHierarchyLevel,
    });
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-8">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-neutral-600 dark:text-neutral-300">Loading role...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-neutral-800 rounded-xl p-8 max-w-md max-h-[90vh] overflow-y-auto">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <p className="mt-4 text-center text-neutral-600 dark:text-neutral-300">
            Failed to load role details
          </p>
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const { role, users, permissionSchema } = data;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${role.isInternal ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
              <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Edit Role: {role.displayName}
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                System name: <code className="bg-neutral-100 dark:bg-neutral-700 px-1 rounded">{role.name}</code>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={editedDisplayName}
                onChange={(e) => {
                  setEditedDisplayName(e.target.value);
                  setHasChanges(true);
                }}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Hierarchy Level
              </label>
              <input
                type="number"
                min="0"
                max="10"
                value={editedHierarchyLevel}
                onChange={(e) => {
                  setEditedHierarchyLevel(parseInt(e.target.value) || 0);
                  setHasChanges(true);
                }}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
              />
              <p className="text-xs text-neutral-500 mt-1">
                Higher levels can access features of lower levels
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Description
            </label>
            <textarea
              value={editedDescription}
              onChange={(e) => {
                setEditedDescription(e.target.value);
                setHasChanges(true);
              }}
              rows={2}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white resize-none"
            />
          </div>

          {/* Info box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium">About Permissions</p>
              <p className="mt-1">
                Click on permission badges to toggle them on/off. The <strong>all_companies</strong> permission
                allows viewing data across all companies (for internal staff).
              </p>
            </div>
          </div>

          {/* Permissions Editor */}
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Permissions
            </h3>
            {editedPermissions && (
              <PermissionEditor
                permissions={editedPermissions}
                permissionSchema={permissionSchema}
                customer={!role.isInternal}
                onChange={handlePermissionsChange}
                disabled={updateMutation.isPending}
              />
            )}
          </div>

          {/* Users with this role */}
          {users && users.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
                Users with this Role ({users.length})
              </h3>
              <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  {users.map(user => (
                    <div
                      key={user.id}
                      className="px-4 py-2 border-b border-neutral-100 dark:border-neutral-700 last:border-b-0 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                          {user.firstName?.[0] || user.email[0].toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-neutral-900 dark:text-white">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-neutral-500">{user.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Delete Section - Only for custom roles */}
        {!isSystemRole && (
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
              >
                <Trash2 className="w-4 h-4" />
                Delete this role
              </button>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-700 dark:text-red-300 font-medium mb-2">
                  Are you sure you want to delete this role?
                </p>
                {hasUsers && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                    This role has {data.users.length} user(s) assigned. Please reassign them to another role first.
                  </p>
                )}
                {deleteMutation.isError && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                    {deleteMutation.error?.response?.data?.error || 'Failed to delete role'}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending || hasUsers}
                    className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {deleteMutation.isPending ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-3 h-3" />
                        Delete Role
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between bg-neutral-50 dark:bg-neutral-900">
          <div className="text-sm text-neutral-500">
            {hasChanges && (
              <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
            >
              {updateMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Success/Error messages */}
        {updateMutation.isSuccess && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <Check className="w-4 h-4" />
            Changes saved successfully
          </div>
        )}
        {updateMutation.isError && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {updateMutation.error?.response?.data?.error || 'Failed to save changes'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Admin Roles Page
 */
/**
 * ConnectWise contact type / flag -> customer role (backend
 * services/customerRoleMapping.js). Applied when someone registers, by a
 * daily sync if switched on, and on demand with a preview first. A role an
 * admin sets by hand is never changed by it.
 */
function ContactTypeMapping() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer-role-mapping'],
    queryFn: () => api.get('/admin/customer-roles/mapping').then((r) => r.data),
  });
  const [draft, setDraft] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => { if (data && !draft) setDraft(data.mapping); }, [data, draft]);

  const save = useMutation({
    mutationFn: (mapping) => api.put('/admin/customer-roles/mapping', mapping),
    onSuccess: (r) => {
      setDraft(r.data.mapping);
      setOutcome({ ok: true, text: 'Saved. New registrations use it now.' });
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: ['customer-role-mapping'] });
    },
    onError: (e) => setOutcome({ ok: false, text: e.response?.data?.error || 'Could not save' }),
  });
  const apply = useMutation({
    mutationFn: (dryRun) => api.post(`/admin/customer-roles/mapping/apply?dryRun=${dryRun ? 1 : 0}`).then((r) => r.data),
    onSuccess: (r) => {
      setPreview(r);
      if (!r.dryRun) {
        setOutcome({ ok: true, text: `Applied: ${r.changes.length} user${r.changes.length === 1 ? '' : 's'} moved.` });
        queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
      }
    },
    onError: (e) => setOutcome({ ok: false, text: e.response?.data?.error || 'Could not apply' }),
  });

  if (isLoading || !draft) return <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading the ConnectWise mapping…</div>;
  if (error) return <div className="text-sm text-red-600 dark:text-red-400">Could not load the ConnectWise mapping.</div>;

  const roleOptions = (allowNone) => (
    <>
      <option value="">Not mapped</option>
      {data.roles.map((r) => <option key={r.name} value={r.name}>{r.displayName}</option>)}
      {allowNone && <option value="none">No portal access</option>}
    </>
  );
  const select = 'w-full sm:w-56 rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-2 py-1.5 text-sm text-neutral-900 dark:text-white';
  const row = 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-b border-neutral-100 dark:border-neutral-700 last:border-0';

  return (
    <div className="mt-8 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 sm:p-6 bg-white dark:bg-neutral-900">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Roles from ConnectWise</h3>
      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 mb-4">
        When someone registers, their role comes from their ConnectWise contact. If several rows match, the most capable
        role wins; &ldquo;No portal access&rdquo; beats everything (use it for a &ldquo;no longer with the company&rdquo; type).
        A role you or a company admin set by hand is never changed by this.
      </p>

      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Contact flags</h4>
      <div className="mb-4">
        {data.flags.map((f) => (
          <div key={f.key} className={row}>
            <span className="text-sm text-neutral-800 dark:text-neutral-200">{f.label}</span>
            <select className={select} value={draft.flags[f.key] || ''} onChange={(e) => setDraft({ ...draft, flags: { ...draft.flags, [f.key]: e.target.value || null } })}>
              {roleOptions(false)}
            </select>
          </div>
        ))}
      </div>

      <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Contact types</h4>
      <div className="mb-4">
        {data.contactTypes.length === 0 && <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">ConnectWise returned no contact types.</p>}
        {data.contactTypes.map((t) => (
          <div key={t.id} className={row}>
            <span className="text-sm text-neutral-800 dark:text-neutral-200 min-w-0 break-words">{t.name}</span>
            <select className={select} value={draft.contactTypes[String(t.id)] || ''} onChange={(e) => setDraft({ ...draft, contactTypes: { ...draft.contactTypes, [String(t.id)]: e.target.value } })}>
              {roleOptions(true)}
            </select>
          </div>
        ))}
      </div>

      <div className={row}>
        <span className="text-sm text-neutral-800 dark:text-neutral-200">Everyone else</span>
        <select className={select} value={draft.defaultRole} onChange={(e) => setDraft({ ...draft, defaultRole: e.target.value })}>
          {data.roles.map((r) => <option key={r.name} value={r.name}>{r.displayName}</option>)}
        </select>
      </div>
      <label className="flex items-start gap-2 mt-4 text-sm text-neutral-700 dark:text-neutral-300">
        <input type="checkbox" className="mt-0.5" checked={draft.syncExisting} onChange={(e) => setDraft({ ...draft, syncExisting: e.target.checked })} />
        <span>Keep roles in step every day: when a contact&apos;s type or flags change in ConnectWise, their role follows. Only people the mapping placed are moved.</span>
      </label>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate(draft)}>
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save mapping
        </button>
        <button type="button" className="btn-outline" disabled={apply.isPending} onClick={() => apply.mutate(true)}>
          Preview for existing users
        </button>
        {outcome && (
          <span className={`text-sm ${outcome.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{outcome.text}</span>
        )}
      </div>

      {preview && preview.dryRun && (
        <div className="mt-4 rounded-md bg-neutral-50 dark:bg-neutral-800 p-4 text-sm">
          <p className="text-neutral-800 dark:text-neutral-200">
            Of {preview.considered} customer user{preview.considered === 1 ? '' : 's'} (excluding roles set by hand),{' '}
            <strong>{preview.changes.length}</strong> would change.
          </p>
          {preview.changes.length > 0 && (
            <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
              {preview.changes.map((c) => (
                <li key={c.userId} className="text-neutral-700 dark:text-neutral-300 break-words">{c.email}: {c.from} → {c.to}</li>
              ))}
            </ul>
          )}
          {preview.noAccess.length > 0 && (
            <p className="mt-2 text-amber-700 dark:text-amber-400">
              {preview.noAccess.length} user{preview.noAccess.length === 1 ? ' is' : 's are'} mapped to no portal access. Nothing is done to them
              automatically; deactivate them under Users if that is right: {preview.noAccess.map((n) => n.email).join(', ')}
            </p>
          )}
          {preview.changes.length > 0 && (
            <button type="button" className="btn-primary mt-3" disabled={apply.isPending} onClick={() => apply.mutate(false)}>
              Apply these changes
            </button>
          )}
        </div>
      )}
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-4">Save the mapping before previewing; the preview uses what is saved.</p>
    </div>
  );
}

export default function AdminRolesPage() {
  const { portalName } = useBranding();
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: async () => {
      const response = await api.get('/admin/roles');
      return response.data;
    }
  });

  const handleRoleCreated = (newRole) => {
    setShowCreateModal(false);
    // Open the edit modal for the new role to set permissions
    setSelectedRoleId(newRole.id);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-700 rounded w-48" />
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-96" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700 dark:text-red-300">
            Failed to load roles: {error.message}
          </p>
        </div>
      </div>
    );
  }

  const { roles } = data;

  // Separate internal and external roles
  const internalRoles = roles.filter(r => r.isInternal);
  const externalRoles = roles.filter(r => !r.isInternal);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
            Roles & Permissions
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-1">
            Manage portal roles and their permissions. Changes affect all users with the role.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Role
        </button>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-700 dark:text-amber-300">
          <p className="font-medium">Important</p>
          <p className="mt-1">
            Changes to role permissions take effect immediately for all users with that role.
            Users may need to refresh their browser to see the changes.
          </p>
        </div>
      </div>

      {/* Internal Roles */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
          <Lock className="w-5 h-5 text-purple-500" />
          Internal Staff Roles
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
          Roles for {portalName} employees with access to admin features and all company data.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {internalRoles.map(role => (
            <RoleCard
              key={role.id}
              role={role}
              onClick={() => setSelectedRoleId(role.id)}
            />
          ))}
        </div>
      </div>

      {/* External Roles */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
          <Unlock className="w-5 h-5 text-blue-500" />
          Customer Roles
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
          What your clients' people can see and do. Every check is enforced on the server. Assign roles under Users, let a company admin assign them on their Team page, or map them from ConnectWise contact types below.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {externalRoles.map(role => (
            <RoleCard
              key={role.id}
              role={role}
              onClick={() => setSelectedRoleId(role.id)}
            />
          ))}
        </div>
        <ContactTypeMapping />
      </div>

      {/* Edit Modal */}
      {selectedRoleId && (
        <RoleEditModal
          roleId={selectedRoleId}
          onClose={() => setSelectedRoleId(null)}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <RoleCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleRoleCreated}
        />
      )}
    </div>
  );
}
